import { IMAGES } from "../lib/site";
import PageHero from "../components/site/PageHero";
import CTASection from "../components/site/CTASection";
import { Wrench, PhoneCall, Hammer, ShieldCheck } from "lucide-react";

const AREAS = [
  {
    icon: Wrench,
    title: "General Repairs",
    copy: "From dripping taps and squeaking hinges to patching plasterwork and shelving that finally sits level — the small jobs that make a home feel cared for.",
  },
  {
    icon: PhoneCall,
    title: "Emergency Call-outs",
    copy: "Leaks, broken locks, damaged doors, storm damage — we answer the phone and turn up when you need us, day or night.",
  },
  {
    icon: Hammer,
    title: "Handyman Work",
    copy: "Flat-pack builds, fixings, mounting, decorating touch-ups, kitchen and bathroom fixes. If it needs doing, put it on the list.",
  },
  {
    icon: ShieldCheck,
    title: "Preventative Upkeep",
    copy: "Regular maintenance visits to catch problems before they grow — silicone, sealants, gutters, hinges and everything else.",
  },
];

export default function PropertyMaintenance() {
  return (
    <>
      <PageHero
        overline="Inside the home"
        title="Property Maintenance"
        subtitle="Repairs, emergency call-outs and dependable handyman work — one number, one team, one standard."
        image={IMAGES.interior}
        testId="property-hero"
      />

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline text-neutral-500">The service</p>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              The right hands for every little thing your home asks of you.
            </h2>
            <p className="mt-8 text-neutral-600 text-lg leading-relaxed max-w-2xl">
              Homes are living things &mdash; they leak, creak, chip and shift. We
              look after each one like it&rsquo;s our own, with clean workmanship, on
              time, and priced fairly.
            </p>
          </div>
        </div>
      </section>

      {/* Grid of areas */}
      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
          {AREAS.map((a) => {
            const Icon = a.icon;
            return (
              <div
                key={a.title}
                className="bg-white p-10 md:p-12"
                data-testid={`pm-area-${a.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={28} strokeWidth={1.5} />
                <h3 className="mt-6 font-serif-display text-3xl leading-tight">
                  {a.title}
                </h3>
                <p className="mt-4 text-neutral-600 leading-relaxed">
                  {a.copy}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Editorial image + text */}
      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 img-hover">
            <img
              src={IMAGES.toolBelt}
              alt="Craftsman tools"
              className="w-full h-[560px] object-cover"
            />
          </div>
          <div className="md:col-span-5">
            <p className="overline text-neutral-500 mb-4">On the tools</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
              Fifteen years, hundreds of jobs, one steady standard.
            </h2>
            <p className="mt-6 text-neutral-600 leading-relaxed">
              We turn up when we say, work tidy, and clean up after ourselves.
              Nothing revolutionary — just what tradesmen used to be.
            </p>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
